import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import { Grid, Box, Alert, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { updateShopName } from '../../middleware/registration_service/updateRequest';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfileData } from '../../context/ProfileDataContext';

// Add validation schema
const schema = Yup.object().shape({
  shopName: Yup.string().required('Shop name is required'),
});

export default function InfoChangeDialog() {
  const { getAccessTokenSilently } = useAuth0();
  const { shopId, shop, setShop } = useProfileData();
  const [open, setOpen] = React.useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-populate the form with the shop's current name so the dialog never
  // opens blank.
  const initialValues = useMemo(
    () => ({
      shopName: shop?.name || '',
    }),
    [shop]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        setSubmitted(true);
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        await updateShopName(values.shopName, shopId, token);

        // Reflect the updated name immediately on the profile without a
        // full page reload.
        setShop((prev) => (prev ? { ...prev, name: values.shopName } : prev));

        setTimeout(() => {
          setSubmitted(false);
          handleClose();
        }, 2000);
      } catch (error) {
        setError('Error updating shop name');
        console.error('Error updating shop name:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSubmitted(false);
    setError(null);
    formik.resetForm();
  };

  return (
    <React.Fragment>
      <IconButton
        aria-label="Edit shop name"
        onClick={handleClickOpen}
        size="small"
        sx={{ color: '#dd8709', ml: 0.5 }}
      >
        <EditIcon sx={{ width: '18px', height: '18px' }} />
      </IconButton>

      <Dialog
        className="shopProfileDialog"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            width: '80%',
            maxWidth: '600px',
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              textAlign: 'center',
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
            id="customized-dialog-title"
          >
            <DriveFileRenameOutlineIcon sx={{ color: '#dd8709' }} />
            Change shop name
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="shopName"
                    fullWidth
                    id="shopName"
                    label="Shop name"
                    value={formik.values.shopName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.shopName && Boolean(formik.errors.shopName)
                    }
                    helperText={
                      formik.touched.shopName && formik.errors.shopName
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              className="buttonHover"
              type="submit"
              disabled={loading}
              fullWidth
              variant="contained"
              sx={{
                mr: '15px',
                ml: '15px',
                mb: '10px',
                mt: '2em',
                bgcolor: '#dd8709',
                color: '#fff',
                fontFamily: 'GFS Didot',
                fontWeight: 'bold',
                p: 1,
                width: '100%',
                '&:hover': { bgcolor: '#b86e08' },
                '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </form>
        {submitted && (
          <Alert
            onClose={() => setSubmitted(false)}
            severity="success"
            sx={{ mt: '0.5em' }}
          >
            Shop name updated successfully
          </Alert>
        )}
        {error && (
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ mt: '0.5em' }}
          >
            {error}
          </Alert>
        )}
      </Dialog>
    </React.Fragment>
  );
}
